import React, { useEffect, useState } from "react";
import api from "../../../api/axiosConfig";
import "../../../center admin css/VitalsExaminationForm.css";
import "../../../center admin css/centerAdmin.css";
import abdominalRegionsImage from "../../../assets/images/abdominal-regions.png.jpg";
import { useS3Uploader, UploadedImageInfo } from "../../../hooks/useS3Uploader";

type BP = { sys?: number | ""; dia?: number | "" };
type LimbBP = { leftArm: BP; rightArm: BP; leftLeg: BP; rightLeg: BP };
type PulseOx = {
  leftUpper?: number | "";
  rightUpper?: number | "";
  leftLower?: number | "";
  rightLower?: number | "";
};
type Vitals = {
  bodyTemp?: number | "";
  pulseRate?: number | "";
  respRate?: number | "";
};
type AudioKey = "heart" | "lung" | "arteries" | "abdomen";
type AudioItem = { file?: File | null; previewUrl?: string | undefined };
type AudioState = Record<AudioKey, AudioItem>;
type AbdomenRegion = {
  finding: "Expected" | "Unexpected" | "";
  pain: boolean;
  mass: boolean;
};
type AbdomenExam = Record<string, AbdomenRegion>;

interface VitalsExaminationFormProps {
  emrId: string;
  onComplete?: () => void;
}

export const VitalsExaminationForm: React.FC<VitalsExaminationFormProps> = ({
  emrId,
  onComplete,
}) => {
  const abdomenRegions = [
    "Right Hypochondriac",
    "Epigastric region",
    "Left hypochondriac",
    "Right Lumbar",
    "Umbilical Region",
    "Left Lumbar region",
    "Right Iliac Region",
    "Hypogastric region",
    "Left Iliac Region",
  ];

  // use your S3 uploader hook (you said you've already added/updated this file)

  const { uploadImage: uploadToS3, uploading, progress } = useS3Uploader(emrId);

  const [forms, setForms] = useState<{
    vitals: Vitals;
    bp: LimbBP;
    ox: PulseOx;
    audio: AudioState;
    abdomen: AbdomenExam;
  }>({
    vitals: { bodyTemp: "", pulseRate: "", respRate: "" },
    bp: {
      leftArm: { sys: "", dia: "" },
      rightArm: { sys: "", dia: "" },
      leftLeg: { sys: "", dia: "" },
      rightLeg: { sys: "", dia: "" },
    },
    ox: { leftUpper: "", rightUpper: "", leftLower: "", rightLower: "" },
    audio: {
      heart: { file: null, previewUrl: undefined },
      lung: { file: null, previewUrl: undefined },
      arteries: { file: null, previewUrl: undefined },
      abdomen: { file: null, previewUrl: undefined },
    },
    abdomen: abdomenRegions.reduce((acc, region) => {
      acc[region] = { finding: "", pain: false, mass: false };
      return acc;
    }, {} as AbdomenExam),
  });

  const [expandedSections, setExpandedSections] = useState({
    basicVitals: true,
    bloodPressure: true,
    pulseOximetry: false,
    abdomen: false,
    auscultation: false,
  });

  const getSignedAudioUrl = async (
    filePath?: string,
  ): Promise<string | undefined> => {
    if (!filePath) return undefined;

    // Already a usable URL
    if (
      filePath.startsWith("http://") ||
      filePath.startsWith("https://") ||
      filePath.startsWith("blob:")
    ) {
      return filePath;
    }

    try {
      const token = sessionStorage.getItem("accessToken");
      if (!token) return undefined;

      const parts = filePath.split("/");

      if (parts.length < 3) {
        console.warn("Invalid audio file path:", filePath);
        return undefined;
      }

      // Example:
      // vitals/auscultation/heart/P-2026-000020-8/file.mpeg
      const module = `${parts[0]}/${parts[1]}`;
      const key = parts.slice(2).join("/");

      const res = await api.S3config.get("/view-url", {
        params: {
          module,
          key,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return res.data?.url;
    } catch (err) {
      console.error("Failed to get signed audio URL:", err);
      return undefined;
    }
  };

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const resetForm = () => {
    setForms({
      vitals: { bodyTemp: "", pulseRate: "", respRate: "" },

      bp: {
        leftArm: { sys: "", dia: "" },
        rightArm: { sys: "", dia: "" },
        leftLeg: { sys: "", dia: "" },
        rightLeg: { sys: "", dia: "" },
      },

      ox: {
        leftUpper: "",
        rightUpper: "",
        leftLower: "",
        rightLower: "",
      },

      audio: {
        heart: { file: null, previewUrl: undefined },
        lung: { file: null, previewUrl: undefined },
        arteries: { file: null, previewUrl: undefined },
        abdomen: { file: null, previewUrl: undefined },
      },

      abdomen: abdomenRegions.reduce((acc, region) => {
        acc[region] = {
          finding: "",
          pain: false,
          mass: false,
        };
        return acc;
      }, {} as AbdomenExam),
    });
  };

  /* ---------------------------------------------------------
      LOAD EXISTING VITALS
  ---------------------------------------------------------- */

  const loadExistingVitals = async () => {
    if (!emrId) return;

    try {
      const res = await api.dataCollectionApi.get(
        `/api/forms/vitals/${encodeURIComponent(emrId)}`,
      );

      const saved = res.data?.data;

      if (!saved) {
        resetForm();
        return;
      }

      const savedBasic = saved.basic ?? {};
      const savedBP = saved.bloodPressure ?? {};
      const savedOx = saved.pulseOximetry ?? {};
      const savedAbdomen = saved["Abdominal Palpation"] ?? {};
      const savedAudio = saved.auscultation ?? {};

      const heartAudioUrl = await getSignedAudioUrl(
        savedAudio.heartAudioUrls?.[0],
      );

      const lungAudioUrl = await getSignedAudioUrl(
        savedAudio.lungAudioUrls?.[0],
      );

      const arteriesAudioUrl = await getSignedAudioUrl(
        savedAudio.arteriesAudioUrls?.[0],
      );

      const abdomenAudioUrl = await getSignedAudioUrl(
        savedAudio.abdomenAudioUrls?.[0],
      );

      const restoredAbdomen: AbdomenExam = abdomenRegions.reduce(
        (acc, region) => {
          const regionData = savedAbdomen?.[region] ?? {};

          acc[region] = {
            finding:
              regionData.Finding === "Unexpected Findings"
                ? "Unexpected"
                : regionData.Finding === "Expected Findings"
                  ? "Expected"
                  : "",
            pain: !!regionData.Pain,
            mass: !!regionData.Mass,
          };

          return acc;
        },
        {} as AbdomenExam,
      );

      setForms({
        vitals: {
          bodyTemp: savedBasic.temperatureF ?? "",
          pulseRate: savedBasic.pulseBpm ?? "",
          respRate: savedBasic.respiratoryRateBpm ?? "",
        },

        bp: {
          leftArm: {
            sys: savedBP.leftArm?.systolic ?? "",
            dia: savedBP.leftArm?.diastolic ?? "",
          },
          rightArm: {
            sys: savedBP.rightArm?.systolic ?? "",
            dia: savedBP.rightArm?.diastolic ?? "",
          },
          leftLeg: {
            sys: savedBP.leftLeg?.systolic ?? "",
            dia: savedBP.leftLeg?.diastolic ?? "",
          },
          rightLeg: {
            sys: savedBP.rightLeg?.systolic ?? "",
            dia: savedBP.rightLeg?.diastolic ?? "",
          },
        },

        ox: {
          leftUpper: savedOx.leftUpperLimb?.value ?? "",
          rightUpper: savedOx.rightUpperLimb?.value ?? "",
          leftLower: savedOx.leftLowerLimb?.value ?? "",
          rightLower: savedOx.rightLowerLimb?.value ?? "",
        },

        audio: {
          heart: {
            file: null,
            previewUrl: heartAudioUrl,
          },
          lung: {
            file: null,
            previewUrl: lungAudioUrl,
          },
          arteries: {
            file: null,
            previewUrl: arteriesAudioUrl,
          },
          abdomen: {
            file: null,
            previewUrl: abdomenAudioUrl,
          },
        },

        abdomen: restoredAbdomen,
      });
    } catch (err: any) {
      if (err?.response?.status === 404) {
        resetForm();
        return;
      }

      console.error("Failed to load existing Vitals:", err);
    }
  };

  useEffect(() => {
    loadExistingVitals();
  }, [emrId]);

  const setVital =
    (field: keyof Vitals) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value === "" ? "" : Number(e.target.value);
      setForms((prev) => ({
        ...prev,
        vitals: { ...prev.vitals, [field]: value },
      }));
    };

  const setBP =
    (limb: keyof LimbBP, key: keyof BP) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value === "" ? "" : Number(e.target.value);
      setForms((prev) => ({
        ...prev,
        bp: { ...prev.bp, [limb]: { ...prev.bp[limb], [key]: value } },
      }));
    };

  const setOxVal =
    (key: keyof PulseOx) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value === "" ? "" : Number(e.target.value);
      setForms((prev) => ({ ...prev, ox: { ...prev.ox, [key]: value } }));
    };

  const onPickAudio =
    (key: AudioKey) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        const previewUrl = URL.createObjectURL(file);
        setForms((prev) => ({
          ...prev,
          audio: { ...prev.audio, [key]: { file, previewUrl } },
        }));
      }
    };

  const removeAudio = (key: AudioKey) => {
    setForms((prev) => ({
      ...prev,
      audio: {
        ...prev.audio,
        [key]: {
          file: null,
          previewUrl: undefined,
        },
      },
    }));
  };

  // Helper: upload a single audio file to S3 and return URL or null.
  async function uploadAudioFileToS3(
    key: AudioKey,
    file: File | undefined | null,
  ): Promise<string | null> {
    if (!file) return null;

    try {
      // moduleName chosen so backend can use it to create subfolder e.g. vitals/auscultation/heart
      const moduleName = `vitals/auscultation/${key}`;
      const result: UploadedImageInfo | null = await uploadToS3(
        file,
        moduleName,
        (p) => {},
      );

      if (result && result.key) {
        return result.key;
      } else {
        console.warn("Upload succeeded but no URL returned for", key);
        return null;
      }
    } catch (err) {
      console.error("Upload to S3 failed for", key, err);
      return null;
    }
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // =========================
    // Validation
    // =========================

    if (forms.vitals.bodyTemp === "") {
      alert("Please enter Body Temperature");
      return;
    }

    if (forms.vitals.pulseRate === "") {
      alert("Please enter Pulse / Heart Rate");
      return;
    }

    if (forms.vitals.respRate === "") {
      alert("Please enter Respiratory Rate");
      return;
    }

    if (forms.bp.leftArm.sys === "") {
      alert("Please enter Left Arm Systolic");
      return;
    }

    if (forms.bp.leftArm.dia === "") {
      alert("Please enter Left Arm Diastolic");
      return;
    }

    if (forms.bp.rightArm.sys === "") {
      alert("Please enter Right Arm Systolic");
      return;
    }

    if (forms.bp.rightArm.dia === "") {
      alert("Please enter Right Arm Diastolic");
      return;
    }

    if (forms.bp.leftLeg.sys === "") {
      alert("Please enter Left Leg Systolic");
      return;
    }

    if (forms.bp.leftLeg.dia === "") {
      alert("Please enter Left Leg Diastolic");
      return;
    }

    if (forms.bp.rightLeg.sys === "") {
      alert("Please enter Right Leg Systolic");
      return;
    }

    if (forms.bp.rightLeg.dia === "") {
      alert("Please enter Right Leg Diastolic");
      return;
    }

    if (forms.ox.leftUpper === "") {
      alert("Please enter Left Upper Pulse Oximetry");
      return;
    }

    if (forms.ox.rightUpper === "") {
      alert("Please enter Right Upper Pulse Oximetry");
      return;
    }

    if (forms.ox.leftLower === "") {
      alert("Please enter Left Lower Pulse Oximetry");
      return;
    }

    if (forms.ox.rightLower === "") {
      alert("Please enter Right Lower Pulse Oximetry");
      return;
    }

    for (const region of abdomenRegions) {
      if (!forms.abdomen[region].finding) {
        alert(`Please select findings for ${region}`);
        return;
      }
    }

    // onComplete && onComplete();

    try {
      // --- 1) Upload audio files to S3 and collect returned URLs ---
      // We'll create the exact keys expected by your backend:
      // heartAudioUrls, lungAudioUrls, arteriesAudioUrls, abdomenAudioUrls
      const audioKeys: AudioKey[] = ["heart", "lung", "arteries", "abdomen"];
      const audioPayload: Record<string, string[]> = {
        heartAudioUrls: [],
        lungAudioUrls: [],
        arteriesAudioUrls: [],
        abdomenAudioUrls: [],
      };

      // sequential upload (safe). If you prefer parallel, we can run Promise.all.
      for (const k of audioKeys) {
        const audioItem = forms.audio[k];
        if (audioItem?.file) {
          const uploadedUrl = await uploadAudioFileToS3(k, audioItem.file);
          if (uploadedUrl) {
            // map to backend field name
            const backendKey = `${k}AudioUrls`; // matches expected names
            audioPayload[backendKey] = [uploadedUrl];
          } else {
            // failed -> keep empty array
            const backendKey = `${k}AudioUrls`;
            audioPayload[backendKey] = [];
          }
        } else {
          const backendKey = `${k}AudioUrls`;
          audioPayload[backendKey] = [];
        }
      }

      // --- 2) Format abdomen data ---
      const formattedAbdomen: Record<string, any> = {};
      for (const region of abdomenRegions) {
        const data = forms.abdomen?.[region] || {};
        const findingValue =
          data.finding === "Unexpected"
            ? "Unexpected Findings"
            : "Expected Findings";
        formattedAbdomen[region] = {
          Finding: findingValue,
          Pain:
            findingValue === "Unexpected Findings" ? !!data.pain : undefined,
          Mass:
            findingValue === "Unexpected Findings" ? !!data.mass : undefined,
        };
      }

      // --- 3) Final payload (includes audioPayload) ---
      const payload = {
        emrId,
        basic: {
          temperatureF: forms.vitals.bodyTemp,
          pulseBpm: forms.vitals.pulseRate,
          respiratoryRateBpm: forms.vitals.respRate,
        },
        bloodPressure: {
          leftArm: {
            systolic: forms.bp.leftArm.sys,
            diastolic: forms.bp.leftArm.dia,
          },
          rightArm: {
            systolic: forms.bp.rightArm.sys,
            diastolic: forms.bp.rightArm.dia,
          },
          leftLeg: {
            systolic: forms.bp.leftLeg.sys,
            diastolic: forms.bp.leftLeg.dia,
          },
          rightLeg: {
            systolic: forms.bp.rightLeg.sys,
            diastolic: forms.bp.rightLeg.dia,
          },
        },
        pulseOximetry: {
          leftUpperLimb: { value: forms.ox.leftUpper },
          rightUpperLimb: { value: forms.ox.rightUpper },
          leftLowerLimb: { value: forms.ox.leftLower },
          rightLowerLimb: { value: forms.ox.rightLower },
        },
        // attach the S3 URLs arrays your backend expects
        auscultation: audioPayload,
        "Abdominal Palpation": formattedAbdomen,
      };

      console.log("📦 Submitting payload:", JSON.stringify(payload, null, 2));

      const res = await api.dataCollectionApi.post(
        "api/forms/vitals",
        payload,
        {
          headers: { "Content-Type": "application/json" },
        },
      );

      if (res.data?.success) {
        alert("Vitals Examination submitted successfully!");

        onComplete && onComplete();

        resetForm();
      } else {
        console.warn("Unexpected response from server:", res.data);
        alert("Unexpected response from server.");
      }
    } catch (err: any) {
      console.error("❌ Error submitting vitals:", err);
      alert(
        "Error submitting vitals: " +
          (err.response?.data?.message ?? err.message),
      );
    }
  };

  return (
    <form className="vitals-form" onSubmit={onSubmit}>
      <div className="vs-section-bar">Vitals Examination</div>

      {/* === Basic Vitals === */}
      <div className="vitals-section">
        {/* <div className="vitals-title">Basic Vitals</div> */}
        <div
          className="vitals-title vitals-collapsible"
          onClick={() => toggleSection("basicVitals")}
        >
          <span>Basic Vitals</span>

          <span className="collapse-icon">
            {expandedSections.basicVitals ? "▼" : "▶"}
          </span>
        </div>
        {expandedSections.basicVitals && (
          <div className="vitals-grid3">
            <div className="vitals-field">
              <label>Body Temperature</label>
              <div className="vitals-inline">
                <input
                  type="number"
                  placeholder="Enter value"
                  value={forms.vitals.bodyTemp}
                  onChange={setVital("bodyTemp")}
                />
                <span className="unit">°F</span>
              </div>
            </div>
            <div className="vitals-field">
              <label>Pulse / Heart Rate</label>
              <div className="vitals-inline">
                <input
                  type="number"
                  placeholder="Enter value"
                  value={forms.vitals.pulseRate}
                  onChange={setVital("pulseRate")}
                />
                <span className="unit">bpm</span>
              </div>
            </div>
            <div className="vitals-field">
              <label>Respiratory Rate</label>
              <div className="vitals-inline">
                <input
                  type="number"
                  placeholder="Enter value"
                  value={forms.vitals.respRate}
                  onChange={setVital("respRate")}
                />
                <span className="unit">bpm</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* === Four-Limb Blood Pressure === */}
      <div className="vitals-section">
        {/* <div className="vitals-title">Four-Limb Blood Pressure</div> */}
        <div
          className="vitals-title vitals-collapsible"
          onClick={() => toggleSection("bloodPressure")}
        >
          <span>Four-Limb Blood Pressure</span>

          <span className="collapse-icon">
            {expandedSections.bloodPressure ? "▼" : "▶"}
          </span>
        </div>
        {expandedSections.bloodPressure && (
          <div className="vitals-bp-grid">
            {(Object.keys(forms.bp) as (keyof LimbBP)[]).map((limb) => (
              <div className="vitals-card" key={limb}>
                <div className="vitals-card-title">
                  {limb.replace(/([A-Z])/g, " $1")}
                </div>
                <div className="vitals-bp-row">
                  {["sys", "dia"].map((key) => (
                    <div className="vitals-bp-field" key={key}>
                      <label>{key === "sys" ? "Systolic" : "Diastolic"}</label>
                      <div className="vitals-inline">
                        <input
                          type="number"
                          placeholder="Enter value"
                          value={forms.bp[limb][key as keyof BP]}
                          onChange={setBP(limb, key as keyof BP)}
                        />
                        <span className="unit">mmHg</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* === Four-Limb Pulse Oximetry === */}
      <div className="vitals-section">
        {/* <div className="vitals-title">Four-Limb Pulse Oximetry</div> */}
        <div
          className="vitals-title vitals-collapsible"
          onClick={() => toggleSection("pulseOximetry")}
        >
          <span>Four-Limb Pulse Oximetry</span>

          <span className="collapse-icon">
            {expandedSections.pulseOximetry ? "▼" : "▶"}
          </span>
        </div>
        {expandedSections.pulseOximetry && (
          <div className="vitals-grid4">
            {(Object.keys(forms.ox) as (keyof PulseOx)[]).map((limb) => (
              <div className="vitals-field" key={limb}>
                <label>{limb.replace(/([A-Z])/g, " $1")}</label>
                <div className="vitals-inline">
                  <input
                    type="number"
                    placeholder="Enter value"
                    value={forms.ox[limb]}
                    onChange={setOxVal(limb)}
                  />
                  <span className="unit">%</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* === Abdominal Examination === */}
      <div className="vitals-section">
        <div
          className="vitals-title vitals-collapsible"
          onClick={() => toggleSection("abdomen")}
        >
          <span>Abdominal Examination (9 Regions)</span>

          <span className="collapse-icon">
            {expandedSections.abdomen ? "▼" : "▶"}
          </span>
        </div>

        {expandedSections.abdomen && (
          <div className="vitals-abdomen">
            {/* LEFT: Existing abdominal examination */}
            <div className="vitals-abdomen-form">
              {Object.entries(forms.abdomen).map(([region, data], i) => (
                <div key={region} className="vitals-abdomen-row">
                  <div className="region-title">
                    {i + 1}. {region}
                  </div>

                  {/* Findings Row */}
                  <div className="findings">
                    <label className="finding-option">
                      <input
                        type="radio"
                        name={`${region}-finding`}
                        checked={data.finding === "Expected"}
                        onChange={() =>
                          setForms((p) => ({
                            ...p,
                            abdomen: {
                              ...p.abdomen,
                              [region]: {
                                finding: "Expected",
                                pain: false,
                                mass: false,
                              },
                            },
                          }))
                        }
                      />
                      Expected Findings
                    </label>

                    <div className="unexpected-block">
                      <label className="finding-option">
                        <input
                          type="radio"
                          name={`${region}-finding`}
                          checked={data.finding === "Unexpected"}
                          onChange={() =>
                            setForms((p) => ({
                              ...p,
                              abdomen: {
                                ...p.abdomen,
                                [region]: {
                                  ...data,
                                  finding: "Unexpected",
                                },
                              },
                            }))
                          }
                        />
                        Unexpected Findings
                      </label>

                      {/* Show Pain & Mass only when Unexpected */}
                      {data.finding === "Unexpected" && (
                        <div className="symptoms">
                          <label
                            className={`symptom-item ${
                              data.pain ? "active" : ""
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={data.pain}
                              onChange={() =>
                                setForms((p) => ({
                                  ...p,
                                  abdomen: {
                                    ...p.abdomen,
                                    [region]: {
                                      ...data,
                                      pain: !data.pain,
                                    },
                                  },
                                }))
                              }
                            />
                            Pain
                          </label>

                          <label
                            className={`symptom-item ${
                              data.mass ? "active" : ""
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={data.mass}
                              onChange={() =>
                                setForms((p) => ({
                                  ...p,
                                  abdomen: {
                                    ...p.abdomen,
                                    [region]: {
                                      ...data,
                                      mass: !data.mass,
                                    },
                                  },
                                }))
                              }
                            />
                            Mass
                          </label>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* RIGHT: Static abdominal region reference image */}
            <div className="vitals-abdomen-reference">
              <img
                src={abdominalRegionsImage}
                alt="Abdominal 9 Regions Reference"
              />
            </div>
          </div>
        )}
      </div>

      {/* === Auscultation === */}
      <div className="vitals-section">
        {/* <div className="vitals-title">Auscultation Audio Uploads</div> */}
        <div
          className="vitals-title vitals-collapsible"
          onClick={() => toggleSection("auscultation")}
        >
          <span>Auscultation Audio Uploads</span>

          <span className="collapse-icon">
            {expandedSections.auscultation ? "▼" : "▶"}
          </span>
        </div>
        {expandedSections.auscultation && (
          <div className="vitals-audio-grid">
            {(Object.keys(forms.audio) as AudioKey[]).map((key) => (
              <div className="vitals-audio-card" key={key}>
                <div className="vitals-audio-icon">☁️</div>
                <div className="vitals-audio-title">
                  {key.charAt(0).toUpperCase() + key.slice(1)} Auscultation
                </div>

                <div className="audio-upload-actions">
                  <label className="vitals-upload-btn">
                    Upload Audio
                    <input
                      type="file"
                      accept="audio/*,video/mpeg,.mpeg,.mp3,.wav,.ogg"
                      onChange={onPickAudio(key)}
                      hidden
                    />
                  </label>
                </div>

                {forms.audio[key].previewUrl && (
                  <div className="audio-preview-box">
                    <audio
                      controls
                      src={forms.audio[key].previewUrl}
                      className="audio-player"
                    />

                    <button
                      type="button"
                      className="audio-remove-btn"
                      onClick={() => removeAudio(key)}
                    >
                      ✕
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="audio-actions">
        <button type="button" className="audio-btn audio-btn-secondary">
          Close
        </button>

        <button type="submit" className="audio-btn audio-btn-primary">
          Submit Assessment
        </button>
      </div>
    </form>
  );
};
